import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

import { loadDriverById } from '@/modules/driver/actions/async/load-driver-by-id';
import { updateDriverInfo } from '@/modules/driver/actions/async/updateDriver';
import { TUpdatePersonalInfoForm } from '@/modules/driver/model/driver';
import { getCurrentDriver } from '@/modules/driver/store/selectors';
import { AppDispatch } from '@/root-store';
import { getCodeAndNumberFromPhone } from '@/shared/utils/phoneUtils';
import { useForm } from '@/shared/view/Form';

import { EditPersonalInfoView } from './view';

interface IProps {
  onNavigateBack: (uri: string) => void;
}

const ROUTE = '/chauffeurs/:driverId/edit-info';

const EditPersonalInfo = ({ onNavigateBack }: IProps) => {
  const driverId = useRouteMatch<{ driverId: string }>(ROUTE)?.params?.driverId;

  const dispatch: AppDispatch = useDispatch();

  const driver = useSelector(getCurrentDriver);

  const navigateBack = useCallback(
    () => onNavigateBack(`/chauffeurs/${driverId}`),
    [driverId, onNavigateBack],
  );

  const postForm = useCallback(
    async (formValues: Parameters<typeof updateDriverInfo>[0]) => {
      const { isSucceed, result = {} } = await dispatch(updateDriverInfo(formValues)).unwrap();

      if (isSucceed && !result.validationErrors && !result.baseErrors) {
        navigateBack();
      }

      return result;
    },
    [dispatch, navigateBack],
  );

  const { number, code } = getCodeAndNumberFromPhone(driver?.phone || '');

  const {
    handleSubmit,
    form: {
      control,
      formState: { isSubmitting },
      resetField,
    },
  } = useForm<TUpdatePersonalInfoForm>({
    onSubmit: postForm,
    defaultValues: {
      phoneNumber: number,
      phoneCode: code,
    },
  });

  useEffect(() => {
    if (!driverId) {
      return;
    }

    dispatch(loadDriverById(driverId));
  }, [dispatch, driverId, resetField]);

  useEffect(() => {
    resetField('phoneCode', { defaultValue: code });
    resetField('phoneNumber', { defaultValue: number });
  }, [resetField, number, code]);

  return (
    <EditPersonalInfoView
      control={control}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      onNavigateBack={navigateBack}
    />
  );
};

export { EditPersonalInfo };
